import { Box } from '@chakra-ui/react';
import { SELECTO_TARGET_CLASS, WIDGETBASE_CLASS } from 'constants/bounding-box';
import { setActiveWidget, store } from 'modules/Editor/store';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store/hooks';
import { selectWidgetProperty } from 'modules/Editor/store/infographSelector';
import { setHoveredWidgetId } from 'modules/Editor/store/widgetControlSlice';
import { selectHoveredWidgetId } from 'modules/Editor/store/widgetSelector';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WidgetId } from 'types/idTypes';
import { ReadOnlyWidgetBase, WidgetBaseProp, useEditor, useGroupIdCache, useWidget } from 'widgets/sdk';

import { GroupWidgetData } from './GroupWidget.types';

// This component creates a clickable area for the entire group
const GroupArea = ({ id, memberWidgetIds }: { id: WidgetId; memberWidgetIds: WidgetId[] }) => {
  const { setWidgetRef, cleanupWidgetBoundingBoxConfig, boundingBox } = useEditor();
  const dispatch = useAppDispatch();
  const [groupRect, setGroupRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const groupRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const calculateGroupRect = useCallback(() => {
    if (!memberWidgetIds || memberWidgetIds.length === 0) return;

    requestAnimationFrame(() => {
      let minLeft = Infinity;
      let minTop = Infinity;
      let maxRight = -Infinity;
      let maxBottom = -Infinity;

      // Find the bounding box that contains all member widgets
      memberWidgetIds.forEach((memberId) => {
        const element = document.getElementById(memberId);
        if (!element) return;

        minLeft = Math.min(minLeft, element.offsetLeft);
        minTop = Math.min(minTop, element.offsetTop);
        maxRight = Math.max(maxRight, element.offsetLeft + element.offsetWidth);
        maxBottom = Math.max(maxBottom, element.offsetTop + element.offsetHeight);
      });

      if (minLeft !== Infinity && minTop !== Infinity && maxRight !== -Infinity && maxBottom !== -Infinity) {
        setGroupRect({
          left: minLeft - 5, // buffer for the group area
          top: minTop - 5,
          width: maxRight - minLeft + 10,
          height: maxBottom - minTop + 10,
        });

        if (boundingBox) {
          setTimeout(() => boundingBox.updateRect(), 0);
        }
      }
    });
  }, [memberWidgetIds, boundingBox]);

  useLayoutEffect(() => {
    calculateGroupRect();
  }, [calculateGroupRect]);

  // ResizeObserver to detect when member widgets change size
  useEffect(() => {
    if (!memberWidgetIds || memberWidgetIds.length === 0) return;

    resizeObserverRef.current = new ResizeObserver(calculateGroupRect);

    memberWidgetIds.forEach((memberId) => {
      const element = document.getElementById(memberId);
      if (element && resizeObserverRef.current) {
        resizeObserverRef.current.observe(element);
      }
    });

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [memberWidgetIds, calculateGroupRect]);

  const safeCleanupRef = useCallback(() => {
    if (!cleanupWidgetBoundingBoxConfig) return;
    cleanupWidgetBoundingBoxConfig(id);
  }, [id, cleanupWidgetBoundingBoxConfig]);

  const safeSetWidgetRef = (ref: HTMLDivElement | null) => {
    if (!setWidgetRef) return null;
    groupRef.current = ref;
    setWidgetRef(id, ref);
  };

  const handleGroupClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target !== e.currentTarget) return;

      dispatch(setActiveWidget(id));
      e.stopPropagation();
    },
    [id, dispatch],
  );

  // update the bounding box when the group rect changes.
  // Needed so we can always have the correct bounding box for the group
  useLayoutEffect(() => {
    if (groupRef.current && groupRect) {
      setTimeout(() => boundingBox?.updateRect(), 0);
    }
  }, [boundingBox, groupRect]);

  const handleMouseEnter = useCallback(() => {
    dispatch(setHoveredWidgetId(id));
  }, [id, dispatch]);

  const handleMouseLeave = useCallback(() => {
    const currentHoveredWidgetId = selectHoveredWidgetId(store.getState());
    if (currentHoveredWidgetId === id) {
      dispatch(setHoveredWidgetId(null));
    }
  }, [id, dispatch]);

  useEffect(() => {
    return () => {
      safeCleanupRef();
    };
  }, [safeCleanupRef]);

  if (!groupRect) return null;

  const style = {
    display: 'block',
    position: 'absolute',
    pointerEvents: 'auto',
    outline: 'none',
    ...groupRect,
  } as React.CSSProperties;

  return (
    <Box
      tabIndex={-1}
      ref={safeSetWidgetRef}
      style={style}
      onClick={handleGroupClick}
      data-testid={`groupwidget-${id}`}
      id={id}
      className={`${SELECTO_TARGET_CLASS} ${WIDGETBASE_CLASS}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export const GroupWidget = ({ getWidgetMemberComponent }: WidgetBaseProp) => {
  const { zIndex, widgetId } = useWidget<GroupWidgetData>();

  // select members directly from redux to avoid conflicts when a widget member is replaced
  const memberWidgetIds =
    useAppSelector(selectWidgetProperty<GroupWidgetData, 'memberWidgetIds'>(widgetId, 'memberWidgetIds')) || [];
  const { boundingBox } = useEditor();

  useGroupIdCache(widgetId, memberWidgetIds);

  useLayoutEffect(() => {
    // redraw the the bounding box after grouping to make sure the group + member bounding box area is correct
    // in case widgets auto-height widgets don't render instantly (e.g. text/table)
    setTimeout(() => boundingBox.updateRect(), 0);
  }, [boundingBox]);

  return (
    <>
      <GroupArea id={widgetId} memberWidgetIds={memberWidgetIds} />
      {memberWidgetIds.map((memberWidgetId: WidgetId) => {
        const GroupMemberWidget = getWidgetMemberComponent!();

        return (
          <GroupMemberWidget
            widgetId={memberWidgetId}
            getWidgetMemberComponent={getWidgetMemberComponent}
            zIndex={zIndex!}
            key={'group-' + memberWidgetId}
          />
        );
      })}
    </>
  );
};

export const ReadOnlyGroupWidget = ({ getWidgetMemberComponent }: WidgetBaseProp) => {
  const { memberWidgetIds, zIndex } = useWidget<GroupWidgetData>();

  return (
    <ReadOnlyWidgetBase>
      {memberWidgetIds.map((memberWidgetId: WidgetId) => {
        const GroupMemberWidget = getWidgetMemberComponent!();
        return (
          <GroupMemberWidget
            key={memberWidgetId}
            widgetId={memberWidgetId}
            getWidgetMemberComponent={getWidgetMemberComponent}
            zIndex={zIndex!}
            isReadOnly
          />
        );
      })}
    </ReadOnlyWidgetBase>
  );
};
